/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * InstructorTrainingVideoCard — first-class entry for the instructor's
 * own development uploads. Distinct visual identity (amber, GraduationCap)
 * from the indigo "Learner AI Review" surface so it's never confused.
 *
 * Surfaces:
 *   • PDI Hub home (top of page).
 *   • ADI Instructor Dashboard home.
 *   • Work Mode FEEDBACK stage (clearly separated from pupil section).
 *
 * Renders:
 *   • Big "Upload My Training Video" button.
 *   • Latest self-review status pill with retry / view actions.
 *   • Expandable history of earlier self-reviews.
 *   • Empty state on first use.
 *
 * Uses /api/lesson-review/my-training. NO duplicated upload pipeline —
 * goes through the same backend with self_review=true.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { resolveVerdict } from "../../utils/verdictLabels";
import {
  GraduationCap, ArrowRight, AlertCircle, CheckCircle2,
  Loader2, RotateCcw, Eye, Clock,
} from "lucide-react";
import InstructorTrainingUploadModal from "./InstructorTrainingUploadModal";
import useBasketAutoReopen from "../../hooks/useBasketAutoReopen";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  awaiting_payment: { tone: "amber",   label: "Awaiting payment", Icon: Clock },
  paid:             { tone: "indigo",  label: "Paid · queuing",   Icon: Loader2 },
  queued:           { tone: "indigo",  label: "Queued",           Icon: Clock },
  extracting_audio: { tone: "indigo",  label: "Extracting audio", Icon: Loader2 },
  transcribing:     { tone: "indigo",  label: "Transcribing",     Icon: Loader2 },
  analysing:        { tone: "indigo",  label: "Grading",          Icon: Loader2 },
  grading:          { tone: "indigo",  label: "Grading",          Icon: Loader2 },
  processing:       { tone: "indigo",  label: "Processing",       Icon: Loader2 },
  complete:         { tone: "emerald", label: "Complete",         Icon: CheckCircle2 },
  completed:        { tone: "emerald", label: "Complete",         Icon: CheckCircle2 },
  failed:           { tone: "rose",    label: "Failed",           Icon: AlertCircle },
};

const TONE_CLS = {
  amber:   "bg-amber-100 text-amber-800",
  indigo:  "bg-indigo-100 text-indigo-800",
  emerald: "bg-emerald-100 text-emerald-800",
  rose:    "bg-rose-100 text-rose-800",
};

const INTENT_LABEL = {
  part3_practice:      "Part 3 practice",
  mentor_review:       "Mentor review",
  teaching_reflection: "Teaching reflection",
  client_centred:      "Client-centred review",
  risk_management:     "Risk management review",
  general:             "General self-review",
};

// Phase C (Feb 13, 2026) — calm review_intent pill mapping. Mirror
// of review_intent_meta.REVIEW_INTENTS labels, intentionally short
// so the pill stays small / non-cluttering as the operator asked.
const REVIEW_INTENT_LABEL = {
  independent_drive: "Independent",
  mock_test:         "Mock test",
  coaching_review:   "Coaching",
  general_drive:     "Drive feedback",
  pdi_part3:         "Part 3",
};

export default function InstructorTrainingVideoCard({ isAdmin = false, className = "", autoOpenWithIntent = null }) {
  const [open, setOpen] = useState(false);
  const [reviewIntent, setReviewIntent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  // P0 May 20, 2026 — iOS Photos picker handoff recovery. If the
  // nested ClipBasketModal unmounts during the picker, this hook
  // re-opens the training upload modal and threads through a
  // resume_basket_id so the basket modal reopens in resume mode.
  const [resumeBasketId, setResumeBasketId] = useState(null);
  useBasketAutoReopen({
    basketOpen: open,
    setBasketOpen: setOpen,
    setResumeBasketId,
    source: "instructor_training_upload",
  });

  // Phase B (Feb 13, 2026) — when the /start-review chooser deep-links
  // into /work?intent=<id>, the parent passes autoOpenWithIntent so
  // this card auto-launches its upload modal with the intent stamped.
  useEffect(() => {
    if (autoOpenWithIntent) {
      setReviewIntent(autoOpenWithIntent);
      setOpen(true);
    }
  }, [autoOpenWithIntent]);

  const fetchItems = useCallback(async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/api/lesson-review/my-training`, {
        withCredentials: true,
      });
      setItems(r.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const onUploaded = () => {
    setOpen(false);
    fetchItems();
    toast.success("Upload received — choose your review type to start.");
  };

  const onRetry = async (id) => {
    const target = items.find((x) => x.id === id);
    if (target && target.status === "awaiting_payment" && !target.paid) {
      window.location.href = `/lesson-review?id=${id}`;
      return;
    }
    setRetrying(id);
    try {
      const { data } = await axios.post(
        `${API}/api/upload-pricing/admin/retry/${id}`,
        {},
        { withCredentials: true }
      );
      if (data.action === "pipeline_kicked") {
        toast.success("Retry kicked — processing restarted.");
      } else if (data.action === "source_missing") {
        toast.error("Source file missing — please re-upload.");
      } else if (data.action === "not_paid") {
        toast.error("Review not yet paid — settle payment first.");
      } else {
        toast.info(`Retry: ${data.action}`);
      }
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const latest = items[0];

  // Lightweight CPD summary — computed client-side from /my-training.
  // Contract (May 4 2026):
  //   • Only self_review=True items (enforced server-side; this endpoint
  //     already filters).
  //   • "this month" = items created in the current calendar month.
  //   • Estimated CPD hours = sum of (duration_seconds / 3600) when
  //     available, otherwise a safe fallback of 0.75 h (45 min) per
  //     completed self-review. Only counts items whose status has reached
  //     complete/completed — drafts and failed uploads don't earn hours.
  //   • No new DB tables — natural onramp for the future CPD Record.
  const { monthCount, monthHours } = (() => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${now.getMonth()}`;
    let count = 0;
    let seconds = 0;
    let fallback = 0;
    for (const r of items) {
      const when = r.created_at ? new Date(r.created_at) : null;
      if (!when) continue;
      if (`${when.getFullYear()}-${when.getMonth()}` !== monthKey) continue;
      count += 1;
      const isFinal = r.status === "complete" || r.status === "completed";
      if (!isFinal) continue;
      if (typeof r.duration_seconds === "number" && r.duration_seconds > 0) {
        seconds += r.duration_seconds;
      } else {
        fallback += 1;
      }
    }
    const totalHours = seconds / 3600 + fallback * 0.75;
    // Round to 1 decimal, but strip trailing .0 for cleanness.
    const pretty = Math.round(totalHours * 10) / 10;
    return { monthCount: count, monthHours: pretty };
  })();

  // Media quarantine — silent. No CTA into lesson-review / clip-basket.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <div
      data-testid="instructor-training-card"
      className={`rounded-2xl bg-gradient-to-br from-amber-900/30 to-slate-900/40 border border-amber-700/40 p-4 ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <GraduationCap className="w-5 h-5 text-amber-300" />
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-300">
          My Training Video · For Instructor Development
        </p>
      </div>

      <button
        type="button"
        data-testid="instructor-training-upload-btn"
        onClick={() => setOpen(true)}
        className="w-full rounded-xl bg-amber-500 hover:bg-amber-400 active:scale-[0.99] transition px-4 py-4 text-left flex items-start gap-3"
      >
        <div className="flex-none w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center">
          <GraduationCap className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-extrabold text-white leading-tight">
            Upload My Training Video
          </p>
          <p className="text-[12px] text-amber-50/90 mt-0.5 leading-snug">
            Analyse my PDI/ADI teaching, coaching, risk management, lesson
            structure, and Part 3 competencies.
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-white flex-none mt-1" />
      </button>

      <p className="text-[10.5px] text-slate-400 mt-2 leading-snug">
        Saves to your CPD / Part 3 history. Never published to a learner Passport.
      </p>

      {/* CPD summary — live count of this month's self-reviews + est. hours */}
      {monthCount > 0 && (
        <div
          data-testid="instructor-training-cpd-summary"
          className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-bold text-emerald-200"
        >
          <CheckCircle2 className="w-3 h-3" />
          {monthCount} self-review{monthCount === 1 ? "" : "s"} this month
          {monthHours > 0 ? ` · ~${monthHours} CPD hour${monthHours === 1 ? "" : "s"} logged` : ""}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-slate-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking your training history…
          </div>
        ) : !latest ? (
          <p className="text-[12px] text-slate-400">
            No self-reviews yet. Your first upload will appear here.
          </p>
        ) : (
          <SelfReviewRow
            review={latest}
            isAdmin={isAdmin}
            onRetry={onRetry}
            retrying={retrying === latest.id}
          />
        )}

        {items.length > 1 && (
          <details className="text-[11px] text-slate-400" data-testid="instructor-training-history">
            <summary className="cursor-pointer hover:text-slate-200">
              View {items.length - 1} earlier self-review{items.length - 1 !== 1 ? "s" : ""}
            </summary>
            <div className="mt-2 space-y-2">
              {items.slice(1).map((r) => (
                <SelfReviewRow
                  key={r.id}
                  review={r}
                  isAdmin={isAdmin}
                  onRetry={onRetry}
                  retrying={retrying === r.id}
                />
              ))}
            </div>
          </details>
        )}
      </div>

      {open && (
        <InstructorTrainingUploadModal
          onClose={() => { setOpen(false); setReviewIntent(null); setResumeBasketId(null); }}
          onUploaded={onUploaded}
          reviewIntent={reviewIntent}
          resumeBasketId={resumeBasketId}
        />
      )}
    </div>
  );
}

function SelfReviewRow({ review, isAdmin, onRetry, retrying }) {
  const tone = STATUS_TONE[review.status] || {
    tone: "indigo", label: review.status, Icon: Clock,
  };
  const isFinal = review.status === "complete" || review.status === "completed";
  const isAwaitingPayment =
    review.status === "awaiting_payment" && !review.paid;
  const canRetry =
    isAdmin && review.paid &&
    review.status !== "complete" && review.status !== "completed";
  const intent = INTENT_LABEL[review.intent] || review.intent;
  const reviewIntentLabel = REVIEW_INTENT_LABEL[review.review_intent] || null;
  // Phase D trust loop (Feb 13, 2026) — show the backend's locked
  // display_verdict next to the intent pill when present. The
  // verdict is the single source of truth for what this review
  // returned, so the instructor's history list reads honestly.
  const verdict = resolveVerdict(review.dual_score || null,
                                 review.review_intent || null);

  return (
    <div
      data-testid={`instructor-training-row-${review.id}`}
      className="rounded-lg bg-slate-800/60 border border-slate-700/50 p-2.5 flex items-center gap-2"
    >
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-bold text-slate-100 truncate">
          {review.filename || "Training video"}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${TONE_CLS[tone.tone]}`}
          >
            <tone.Icon
              className={`w-2.5 h-2.5 ${
                tone.tone === "indigo" && !isFinal ? "animate-spin" : ""
              }`}
            />
            {tone.label}
          </span>
          {intent && (
            <span className="text-[10px] text-slate-400">{intent}</span>
          )}
          {reviewIntentLabel && (
            <span
              data-testid={`instructor-training-row-intent-${review.id}`}
              className="text-[9.5px] text-slate-400 px-1.5 py-0.5 rounded-full border border-slate-700/50 bg-slate-800/40"
            >
              {reviewIntentLabel}
            </span>
          )}
          {isFinal && verdict.verdict && (
            <span
              data-testid={`instructor-training-row-verdict-${review.id}`}
              data-display-verdict={verdict.verdict}
              className={`text-[9.5px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${verdict.toneClass}`}
            >
              {verdict.label}
            </span>
          )}
        </div>
        {review.status === "failed" && review.error && (
          <p className="text-[10.5px] text-rose-300 mt-1 line-clamp-2">
            {review.error}
          </p>
        )}
      </div>
      {isAwaitingPayment && (
        <Link
          to={`/lesson-review?id=${review.id}`}
          data-testid={`instructor-training-pay-${review.id}`}
          className="flex-none px-2 py-1 rounded-md bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] hover:brightness-110 text-black text-[10.5px] font-extrabold"
        >
          Pay for review
        </Link>
      )}
      {isFinal && (
        <Link
          to={`/lesson-review?id=${review.id}`}
          data-testid={`instructor-training-view-${review.id}`}
          className="flex-none px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[10.5px] font-bold hover:bg-emerald-500/25"
        >
          <Eye className="w-3 h-3 inline mr-1" /> View
        </Link>
      )}
      {canRetry && (
        <button
          type="button"
          data-testid={`instructor-training-retry-${review.id}`}
          onClick={() => onRetry(review.id)}
          disabled={retrying}
          className="flex-none px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[10.5px] font-bold hover:bg-amber-500/25 disabled:opacity-50"
        >
          <RotateCcw className={`w-3 h-3 inline mr-1 ${retrying ? "animate-spin" : ""}`} />
          {retrying ? "Retrying" : "Retry"}
        </button>
      )}
    </div>
  );
}
